import { Text } from "@chakra-ui/react";
import { FiAperture, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import type { QuoteTemplate } from "../../../api/quote-templates-api";

export interface TemplateListProps {
	templates: QuoteTemplate[];
	onCreate: () => void;
	onDelete: (templateId: number) => void;
	onEdit: (template: QuoteTemplate) => void;
}

/**
 * Header + list view. Rendered when the editor is closed; owns nothing —
 * pure presentation driven by the orchestrator.
 */
export const TemplateList: React.FC<TemplateListProps> = ({
	templates,
	onCreate,
	onEdit,
	onDelete,
}) => {
	return (
		<>
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Quote Templates
					</Text>
					<Text className="text-sm text-gray-500">
						Create reusable templates to quickly set up quote tables with
						predefined columns and rows.
					</Text>
				</div>
				<Button
					size="sm"
					btntype="primary"
					onClick={onCreate}
					data-testid="create-template-btn"
				>
					<FiPlus className="mr-1" />
					New Template
				</Button>
			</div>

			{templates.length === 0 && (
				<div
					className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12"
					data-testid="no-templates"
				>
					<FiAperture className="mb-3 text-gray-400" size={32} />
					<Text className="text-sm text-gray-500">
						No templates yet. Create one to get started.
					</Text>
				</div>
			)}

			{templates.length > 0 && (
				<div className="space-y-2" data-testid="template-list">
					{templates.map((template) => (
						<div
							key={template.id}
							className="flex items-center justify-between rounded-lg border border-[#EAECF0] bg-white px-4 py-3 shadow-sm cursor-pointer hover:bg-blue-50"
							data-testid={`template-item-${template.id}`} onClick={() => onEdit(template)}
						>
							<div className="flex items-center gap-3">
								<FiAperture className="text-primary-500" size={18} />
								<div>
									<Text className="font-roboto text-sm font-medium text-gray-900">
										{template.name}
									</Text>
									<Text className="text-xs text-gray-500">
										{template.columns.length} columns,{" "}
										{template.rows.length} rows
									</Text>
								</div>
							</div>
							<div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
								<button
									type="button"
									className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									onClick={() => onEdit(template)}
									title="Edit template"
									aria-label={`Edit template ${template.name}`}
									data-testid={`edit-template-${template.id}`}
								>
									<FiEdit2 size={14} />
								</button>
								<button
									type="button"
									className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600"
									onClick={() => onDelete(template.id)}
									title="Delete template"
									aria-label={`Delete template ${template.name}`}
									data-testid={`delete-template-${template.id}`}
								>
									<FiTrash2 size={14} />
								</button>
							</div>
						</div>
					))}
				</div>
			)}
		</>
	);
};
